"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";

const NAV_ITEMS = [
  {
    label: "Membership",
    href: "/membership",
    dropdown: [
      { label: "Private membership", href: "/membership/private" },
      { label: "Corporate membership", href: "/membership/corporate" },
    ],
  },
  {
    label: "Services",
    href: "/services",
    dropdown: [
      { label: "Lifestyle", href: "/services/lifestyle" },
      { label: "Corporate", href: "/services/corporate" },
    ],
  },
  { label: "Events", href: "/events" },
  { label: "About", href: "/about" },
  { label: "Contact", href: "/contact" },
];

function ChevronDown({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path
        d="M6 9l6 6 6-6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ArrowUpRight({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="M7 17L17 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M10 7h7v7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function normalizeLocale(pathname: string | null): "fr" | "en" {
  if (!pathname) return "fr";
  const seg = pathname.split("/").filter(Boolean)[0];
  return seg === "en" ? "en" : "fr";
}

function stripLocale(pathname: string | null) {
  if (!pathname) return "/";
  const parts = pathname.split("/").filter(Boolean);
  const first = parts[0];
  if (first === "fr" || first === "en") {
    const rest = parts.slice(1);
    return "/" + rest.join("/");
  }
  return pathname;
}

export default function Header() {
  const pathname = usePathname();
  const locale = useMemo(() => normalizeLocale(pathname), [pathname]);
  const basePath = useMemo(() => stripLocale(pathname), [pathname]);

  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 6);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpenMenu(null);
  }, [pathname]);

  const linkForLocale = (targetLocale: "fr" | "en") => {
    const suffix = basePath.startsWith("/") ? basePath : `/${basePath}`;
    return `/${targetLocale}${suffix === "/" ? "" : suffix}`;
  };

  const navLinkClass =
    "text-[12px] font-light uppercase tracking-[0.08em] text-white/80 transition hover:text-white";

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <div className="mx-auto max-w-[1400px] px-6 pt-4 transition-all duration-300">
        <div
          className={[
            "relative flex h-14 items-center rounded-full px-5",
            "bg-black/45 backdrop-blur-md border border-white/10",
            scrolled
              ? "shadow-[0_10px_40px_rgba(0,0,0,0.45)]"
              : "shadow-[0_10px_30px_rgba(0,0,0,0.28)]",
          ].join(" ")}
        >
          {/* Left - Logo placeholder */}
          <div className="flex min-w-[180px] items-center gap-3">
            <Link href={`/${locale}`} className="inline-flex items-center gap-3" aria-label="Welconcept">
              <span className="h-9 w-9 rounded-full border border-white/20 bg-white/5" />
            </Link>
          </div>

          {/* Center - Navigation */}
          <nav className="hidden flex-1 items-center justify-center md:flex">
            <ul className="flex items-center gap-10">
              {NAV_ITEMS.map((item) => {
                const hasDropdown = !!item.dropdown?.length;
                const isOpen = openMenu === item.label;

                // underline + arrow flip ONLY for Membership & Services (comme demandé)
                const fancyHover = item.label === "Membership" || item.label === "Services";

                return (
                  <li
                    key={item.label}
                    className="relative"
                    onMouseEnter={() => hasDropdown && setOpenMenu(item.label)}
                    onMouseLeave={() => hasDropdown && setOpenMenu(null)}
                  >
                    {hasDropdown ? (
                      <button
                        type="button"
                        className={[
                          fancyHover ? "group inline-flex items-center gap-2" : "inline-flex items-center gap-2",
                          navLinkClass,
                          "relative",
                        ].join(" ")}
                        onClick={() => setOpenMenu(isOpen ? null : item.label)}
                        aria-expanded={isOpen}
                      >
                        <span>{item.label}</span>

                        {/* Arrow flip */}
                        <ChevronDown
                          className={[
                            "h-4 w-4 text-white/70 transition-transform duration-200",
                            fancyHover ? "group-hover:rotate-180" : "",
                            isOpen ? "rotate-180" : "",
                          ].join(" ")}
                        />

                        {/* Underline (only fancy items) */}
                        {fancyHover && (
                          <span
                            className={[
                              "pointer-events-none absolute -bottom-[10px] left-0 h-[1px] w-full",
                              "origin-left scale-x-0 bg-white/55 transition-transform duration-200",
                              "group-hover:scale-x-100",
                              isOpen ? "scale-x-100" : "",
                            ].join(" ")}
                          />
                        )}
                      </button>
                    ) : (
                      <Link href={`/${locale}${item.href}`} className={navLinkClass}>
                        {item.label}
                      </Link>
                    )}

                    {hasDropdown && isOpen && (
                      <div
                        className={[
                          "absolute left-1/2 top-[calc(100%+10px)] -translate-x-1/2",
                          "min-w-[240px] rounded-2xl border border-white/10 bg-black/70 backdrop-blur-md",
                          "p-2 shadow-[0_20px_60px_rgba(0,0,0,0.55)]",
                        ].join(" ")}
                      >
                        <div className="grid gap-1">
                          {item.dropdown!.map((d) => (
                            <Link
                              key={d.href}
                              href={`/${locale}${d.href}`}
                              className={[
                                "group flex items-center justify-between rounded-xl px-3 py-2.5",
                                "text-[12px] font-light uppercase tracking-[0.06em]",
                                "text-white/80 hover:bg-white/10 hover:text-white",
                              ].join(" ")}
                            >
                              <span>{d.label}</span>
                              <ArrowUpRight className="h-4 w-4 text-white/60 opacity-0 transition group-hover:opacity-100" />
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Right - CTA + Locale */}
          <div className="flex min-w-[180px] items-center justify-end gap-4">
            <Link
              href={`/${locale}/membership`}
              className={[
                "hidden whitespace-nowrap rounded-full bg-white px-6 py-2",
                "text-[11px] font-light uppercase text-black",
                "transition hover:bg-white/90 md:inline-flex",
              ].join(" ")}
            >
              Become a member
            </Link>

            <div className="flex items-center gap-2 text-[12px] font-light uppercase tracking-[0.08em] text-white/70">
              <Link
                href={linkForLocale("fr")}
                className={locale === "fr" ? "text-white" : "hover:text-white"}
              >
                FR
              </Link>
              <span className="text-white/35">/</span>
              <Link
                href={linkForLocale("en")}
                className={locale === "en" ? "text-white" : "hover:text-white"}
              >
                EN
              </Link>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}